import { api } from '../axios'
import type { ApiResponse, School } from '../../../types'

export const schoolsApi = {
  /** Ultra-fast lightweight endpoint for Teacher Transfer Map */
  getMapData: () =>
    api.get<ApiResponse<School[]>>('/schools/map-data'),

  /** List schools (active only). Non-active schools are excluded until reactivated. */
  getAll: (page = 1, limit = 10, management?: string, blockOffice?: string) => {
    let url = `/schools?page=${page}&limit=${limit}&includeNonActive=false`
    if (management && management !== 'all') {
      url += `&management=${encodeURIComponent(management)}`
    }
    if (blockOffice && blockOffice !== 'all') {
      url += `&blockOffice=${encodeURIComponent(blockOffice)}`
    }
    return api.get<ApiResponse & { pagination: any }>(url)
  },
  
  getById: (id: string) => 
    api.get<ApiResponse<School>>(`/schools/${id}`),
  
  getBySchoolId: (schoolId: string, includeNonActive = false) => 
    api.get<ApiResponse<School>>(
      `/schools/school-id/${encodeURIComponent(schoolId)}${includeNonActive ? '?includeNonActive=true' : ''}`
    ),

  /** Returns true if a non-deleted school with this business ID already exists. */
  async schoolIdExists(schoolId: string): Promise<boolean> {
    try {
      const res = await api.get<ApiResponse<School>>(
        `/schools/school-id/${encodeURIComponent(schoolId)}?includeNonActive=true`
      )
      return Boolean(res.data.success && res.data.data)
    } catch (error: any) {
      if (error.response?.status === 404) return false
      throw error
    }
  },

  /** Returns true if a non-deleted school with this phone number already exists. */
  async phoneExists(phone: string): Promise<boolean> {
    try {
      const res = await api.get<ApiResponse<{ id: number; school_id: string; school_phone: string | null }>>(
        `/schools/phone/${encodeURIComponent(phone)}`
      )
      return Boolean(res.data.success && res.data.data)
    } catch (error: any) {
      if (error.response?.status === 404) return false
      throw error
    }
  },

  // Avoid browser/proxy caching when immediately reopening edit
  getBySchoolIdFresh: (schoolId: string) =>
    api.get<ApiResponse<School>>(`/schools/school-id/${schoolId}?_=${Date.now()}`),

  /** List schools by district name (master-data driven string field). */
  getByDistrictName: (districtName: string) =>
    api.get<ApiResponse<School[]>>(`/schools/district/${encodeURIComponent(districtName)}`),
  
  /** List schools by RD block name (master-data driven string field). */
  getByRdBlockName: (rdBlockName: string) =>
    api.get<ApiResponse<School[]>>(`/schools/rdblock/${encodeURIComponent(rdBlockName)}`),
  
  create: (data: School) => 
    api.post<ApiResponse<School>>('/schools', data),
  
  update: (id: string, data: Partial<School>) => 
    api.put<ApiResponse<School>>(`/schools/${id}`, data),
  
  updateBySchoolId: (schoolId: string, data: Partial<School>) => 
    api.put<ApiResponse<School>>(`/schools/school-id/${schoolId}`, data),

  /** HOI: submit school update request for approval (own school) */
  getMyUpdateRequest: () =>
    api.get<ApiResponse>(`/schools/me/update-request`),
  submitMyUpdateRequest: (data: Record<string, unknown>) =>
    api.post<ApiResponse>(`/schools/me/update-request`, data),

  /** DEO/SDEO/HOI: update request for a specific school */
  getSchoolUpdateRequest: (schoolId: string) =>
    api.get<ApiResponse>(`/schools/school-id/${encodeURIComponent(schoolId)}/update-request`),
  submitSchoolUpdateRequest: (schoolId: string, data: Record<string, unknown>) =>
    api.post<ApiResponse>(`/schools/school-id/${encodeURIComponent(schoolId)}/update-request`, data),

  /** Admin: list/approve/reject school update requests */
  adminListUpdateRequests: (page = 1, limit = 20) => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('limit', String(limit))
    return api.get<ApiResponse>(`/schools/update-requests/admin/pending?${params.toString()}`)
  },
  adminPendingUpdateRequestsCount: () =>
    api.get<ApiResponse>(`/schools/update-requests/admin/pending-count`),
  adminApproveUpdateRequest: (id: number) =>
    api.patch<ApiResponse>(`/schools/update-requests/admin/${id}/approve`),
  adminRejectUpdateRequest: (id: number, remarks: string) =>
    api.patch<ApiResponse>(`/schools/update-requests/admin/${id}/reject`, { remarks }),
  adminReviewUpdateRequest: (id: number, approveKeys: string[], rejectNotes: Record<string, string>) =>
    api.patch<ApiResponse>(`/schools/update-requests/admin/${id}/review`, { approveKeys, rejectNotes }),
  
  delete: (id: string) => 
    api.delete<ApiResponse>(`/schools/${id}`),
  
  getNonActive: (page = 1, limit = 50) => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    return api.get<ApiResponse & { pagination: any }>(`/schools/non-active?${params.toString()}`)
  },
  
  exportNonActive: () => {
    return api.get(`/schools/non-active/export`, { responseType: 'arraybuffer' })
  },
  
  markNonActive: (id: string, data: { non_active_date: string }) =>
    api.patch<ApiResponse>(`/schools/${id}/mark-non-active`, data),
  
  reactivate: (id: string) =>
    api.patch<ApiResponse>(`/schools/${id}/reactivate`),
  
  deleteBySchoolId: (schoolId: string) => 
    api.delete<ApiResponse>(`/schools/school-id/${schoolId}`),

  /** Request permanent (hard) delete. Requires reason. Super Admin: executes immediately. Admin: creates request for Super Admin approval. */
  requestPermanentDeleteBySchoolId: (schoolId: string, reason: string) =>
    api.delete<ApiResponse>(`/schools/school-id/${schoolId}/permanent`, { data: { reason } }),
  
  search: (query: string, management?: string, blockOffice?: string) => {
    let url = `/schools/search?q=${encodeURIComponent(query)}`
    if (management && management !== 'all') {
      url += `&management=${encodeURIComponent(management)}`
    }
    if (blockOffice && blockOffice !== 'all') {
      url += `&blockOffice=${encodeURIComponent(blockOffice)}`
    }
    return api.get<ApiResponse<School[]>>(url)
  },
  
  getStats: () => 
    api.get<ApiResponse>('/schools/stats'),
}

